/**
 * gradientEditorFactory — Bridge between section-level values/onChange
 * and GradientEditor's object-level value/onChange interface.
 *
 * Two factory functions:
 * - createGradientEditorComponent: for ButtonStyleSections (flat values map with objectPath)
 * - createListGradientEditorComponent: for V2ListStyleSections (nested step object)
 */

import React, { useCallback } from 'react';
import type { GradientOverlay } from '@segmently/schema-models/v2';
import { GradientEditor } from './GradientEditor';
import { getValueByPath } from '../../../utils/fieldOperations';

interface GradientEditorWrapperProps {
  values: Record<string, unknown>;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
}

/**
 * Create a GradientEditor wrapper for ButtonStyleSections.
 * Reads from flat values map (populated by getButtonStyleValues via objectPath).
 * Passes button's backgroundColor as baseColor for gradient derivation.
 *
 * @param basePath - TapTarget base path (e.g., 'content.actionBar.primary')
 */
export function createGradientEditorComponent(
  basePath: string,
): React.ComponentType<GradientEditorWrapperProps> {
  const gradientPath = `${basePath}.appearance.gradient`;
  const bgColorPath = `${basePath}.appearance.backgroundColor`;

  const GradientEditorWrapper: React.FC<GradientEditorWrapperProps> = ({
    values,
    onChange,
    disabled,
  }) => {
    const gradient = (values[gradientPath] as GradientOverlay | null) ?? null;
    const bgColor = (values[bgColorPath] as string | undefined) ?? undefined;

    const handleChange = useCallback(
      (newGradient: GradientOverlay | null) => {
        onChange(gradientPath, newGradient);
      },
      [onChange],
    );

    return (
      <GradientEditor
        value={gradient}
        onChange={handleChange}
        disabled={disabled}
        baseColor={bgColor}
      />
    );
  };

  GradientEditorWrapper.displayName = `GradientEditorWrapper(${basePath})`;
  return GradientEditorWrapper;
}

/**
 * Create a GradientEditor wrapper for V2ListStyleSections.
 * Reads from nested step object via getValueByPath (step is passed as values).
 *
 * @param gradientPath - Full dot-notation path to gradient (e.g., 'content.options.appearance.gradient')
 */
export function createListGradientEditorComponent(
  gradientPath: string,
): React.ComponentType<GradientEditorWrapperProps> {
  const ListGradientEditorWrapper: React.FC<GradientEditorWrapperProps> = ({
    values,
    onChange,
    disabled,
  }) => {
    const gradient = getValueByPath<GradientOverlay>(values, gradientPath) ?? null;

    const handleChange = useCallback(
      (newGradient: GradientOverlay | null) => {
        onChange(gradientPath, newGradient);
      },
      [onChange],
    );

    return (
      <GradientEditor
        value={gradient}
        onChange={handleChange}
        disabled={disabled}
      />
    );
  };

  ListGradientEditorWrapper.displayName = `ListGradientEditorWrapper(${gradientPath})`;
  return ListGradientEditorWrapper;
}
